/* Hoppo fundraise — biermeter + add-to-cart voor sections/hoppo-fundraise.liquid.

   Add-to-cart: posts to /cart/add.js and dispatches the same CartLinesUpdateEvent
   as Horizon's product-form.js, so the cart drawer re-renders and the header
   bubble updates. Horizon only auto-opens the drawer when the theme setting is
   on, so we open it ourselves after a successful add. Without JS the form posts
   natively to /cart/add and lands on the cart page.

   Biermeter: the amount is rendered server-side from inventory (see the section
   comment). Every 30 seconds, while the tab is visible, the section is re-fetched
   through the Section Rendering API and the beer slides to the new position. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const REFRESH_INTERVAL = 30000;
const COUNT_DURATION = 1600;
const STATUS_DURATION = 6000;

const euro = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Biermeter ───────────────────────────────────────────────────────────── */

/** @param {HTMLElement} meter */
function readMeter(meter) {
  return {
    raised: Number(meter.dataset.raised) || 0,
    goal: Number(meter.dataset.goal) || 300000,
    contributions: Number(meter.dataset.contributions) || 0,
  };
}

/**
 * @param {HTMLElement} meter
 * @param {{ raised: number, goal: number, contributions: number }} next
 */
function updateMeter(meter, next) {
  const previous = readMeter(meter);
  if (previous.raised === next.raised && previous.contributions === next.contributions) return;

  const percent = (next.raised / next.goal) * 100;

  meter.dataset.raised = String(next.raised);
  meter.dataset.contributions = String(next.contributions);
  // The beer and the fill follow this variable through their CSS transition.
  meter.style.setProperty('--hoppo-meter-target', `${Math.min(percent, 100).toFixed(2)}%`);
  meter.classList.toggle('is-complete', next.raised >= next.goal);

  const track = meter.querySelector('[data-hoppo-meter-track]');
  if (track) {
    track.setAttribute('aria-valuenow', String(Math.min(Math.floor(next.raised / 100), next.goal / 100)));
    track.setAttribute('aria-valuetext', `${euro.format(next.raised / 100)} van ${euro.format(next.goal / 100)}`);
  }

  const percentEl = meter.querySelector('[data-hoppo-meter-percent]');
  if (percentEl) percentEl.textContent = `${Math.floor(percent)}%`;

  const contributionsEl = meter.querySelector('[data-hoppo-meter-contributions]');
  if (contributionsEl) contributionsEl.textContent = String(next.contributions);

  const doneEl = meter.querySelector('[data-hoppo-meter-done]');
  if (doneEl) doneEl.hidden = next.raised < next.goal;

  const raisedEl = meter.querySelector('[data-hoppo-meter-raised]');
  if (raisedEl) countTo(raisedEl, previous.raised, next.raised);
}

/**
 * Count the euro amount up (or down) so it travels together with the beer.
 * @param {Element} el
 * @param {number} fromCents
 * @param {number} toCents
 */
function countTo(el, fromCents, toCents) {
  if (prefersReducedMotion()) {
    el.textContent = euro.format(toCents / 100);
    return;
  }

  const start = performance.now();
  const step = (now) => {
    const t = Math.min((now - start) / COUNT_DURATION, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = euro.format((fromCents + (toCents - fromCents) * eased) / 100);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** @param {HTMLElement} root */
function initMeter(root) {
  const meter = root.querySelector('[data-hoppo-meter]');
  const sectionId = root.dataset.sectionId;
  if (!meter || !sectionId || window.Shopify?.designMode) return;

  let busy = false;

  const refresh = async () => {
    if (busy || document.visibilityState !== 'visible') return;
    busy = true;
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-hoppo-meter]');
      if (fresh instanceof HTMLElement) updateMeter(meter, readMeter(fresh));
    } catch (error) {
      // A missed refresh is harmless; the next tick tries again.
    } finally {
      busy = false;
    }
  };

  setInterval(refresh, REFRESH_INTERVAL);
  document.addEventListener('visibilitychange', refresh);
}

/* ── Add-to-cart ─────────────────────────────────────────────────────────── */

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    // @ts-ignore — method defined in component-cart-items.js
    return cartItems.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

function openCart() {
  /** @type {HTMLElement & { open?: () => void, isOpen?: boolean } | null} */
  const drawer = document.querySelector('theme-drawer#cart-drawer');

  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
  } else {
    window.location.href = Theme.routes.cart_url || '/cart';
  }
}

/**
 * @param {Element | null} el
 * @param {string} message
 * @param {'success' | 'error'} type
 */
function showStatus(el, message, type) {
  if (!el) return;
  el.textContent = message;
  el.dataset.state = type;
  clearTimeout(Number(el.dataset.timeout));
  el.dataset.timeout = String(
    setTimeout(() => {
      el.textContent = '';
      delete el.dataset.state;
    }, STATUS_DURATION)
  );
}

/** @param {HTMLFormElement} form */
function initTierForm(form) {
  const button = form.querySelector('button[type="submit"]');
  const status =
    form.closest('.hoppo-fund-tier, .hoppo-fund-donate')?.querySelector('[data-hoppo-fund-status]') ?? null;
  const successMessage = form.dataset.hoppoFundSuccess || 'Toegevoegd aan je winkelwagen. Proost! 🍻';

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const quantity = Number(formData.get('quantity')) || 1;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      const cart = await fetchCart();

      const detail = {
        items: cart.items,
        source: 'hoppo-fundraise',
        sourceId: form.id,
        itemCount: quantity,
        productId: form.dataset.productId,
      };

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, didError: true },
        });
        form.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        showStatus(status, result.description || result.message || 'Er ging iets mis. Probeer het opnieuw.', 'error');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, sections: result.sections, didError: false },
      });

      showStatus(status, successMessage, 'success');
      openCart();
    } catch (error) {
      deferred.reject(error);
      form.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      // Last resort: the native form post still gets the bijdrage in the cart.
      form.submit();
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
}

/**
 * Algemene donatie: the amount chips fill the euro input (which is the cart
 * quantity — the product costs €1), and typing keeps the matching chip active.
 * @param {HTMLFormElement} form
 */
function initDonationAmounts(form) {
  const input = form.querySelector('[data-hoppo-fund-quantity]');
  const chips = Array.from(form.querySelectorAll('[data-hoppo-fund-amount]'));
  if (!(input instanceof HTMLInputElement)) return;

  const syncChips = () => {
    chips.forEach((chip) => {
      if (!(chip instanceof HTMLElement)) return;
      chip.classList.toggle('is-active', Number(chip.dataset.hoppoFundAmount) === Number(input.value));
    });
  };

  chips.forEach((chip) => {
    if (!(chip instanceof HTMLElement)) return;
    chip.addEventListener('click', () => {
      input.value = chip.dataset.hoppoFundAmount ?? '';
      syncChips();
    });
  });
  input.addEventListener('input', syncChips);
  syncChips();
}

/* ── Init ────────────────────────────────────────────────────────────────── */

/** @param {ParentNode} scope */
function init(scope = document) {
  scope.querySelectorAll('[data-hoppo-fund]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.hoppoFundReady) return;
    root.dataset.hoppoFundReady = 'true';

    initMeter(root);
    root.querySelectorAll('form[data-hoppo-fund-add]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      initTierForm(form);
      if (form.hasAttribute('data-hoppo-fund-donate')) initDonationAmounts(form);
    });
  });
}

init();

// Theme editor: re-init when the section is re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
